import { HttpErrorResponse } from '@angular/common/http';
import type { IAccountingFileRefusal } from '../models/closing-setup.model';

/**
 * REV-22007 — how the two effects of the file route read a failed request (ADR-0020).
 *
 * In a file of their own, with nothing but `HttpErrorResponse` imported, so the rules are testable in
 * bare Node: the effects files pull the users library (and with it AngularFire), which the vitest
 * runner cannot load.
 */

const PAYLOAD_TOO_LARGE = 413;
const UNSUPPORTED_MEDIA_TYPE = 415;
const HTTP_CONFLICT = 409;

/** The one refusal of `POST …/abcp/sync/file` that is worth waiting out. */
const SCAN_PENDING_KEY = 'abcp.fileImport.scanPending';
/** First wait 2 s, doubling, capped at 15 s — the scan of a small spreadsheet usually takes seconds. */
const SCAN_RETRY_FIRST_MS = 2_000;
const SCAN_RETRY_CAP_MS = 15_000;
/** 42 retries ≈ 10 minutes at the cap — the same ceiling the launch pipeline waits. */
export const SCAN_RETRY_MAX = 42;

/**
 * Any failed upload as ONE refusal shape, so the screen has one thing to render.
 *
 * The 422 body already is one (`{ messageKey, params?, rows }`). The gate's own answers are not: 413
 * and 415 come from the upload pipes before the handler runs and carry Nest's generic body, so they
 * are named here. Anything else — offline, a 500 — falls back to a generic key.
 */
export function accountingFileRefusalOf(error: unknown): IAccountingFileRefusal {
    if (error instanceof HttpErrorResponse) {
        const body = error.error as Partial<IAccountingFileRefusal> | null;
        if (typeof body?.messageKey === 'string' && body.messageKey.startsWith('abcp.')) {
            return {
                messageKey: body.messageKey,
                ...(body.params === undefined ? {} : { params: body.params }),
                rows: Array.isArray(body.rows) ? body.rows : [],
            };
        }
        if (error.status === PAYLOAD_TOO_LARGE) {
            return { messageKey: 'abcp.fileImport.tooLarge', rows: [] };
        }
        if (error.status === UNSUPPORTED_MEDIA_TYPE) {
            return { messageKey: 'abcp.fileImport.wrongType', rows: [] };
        }
    }

    return { messageKey: 'abcp.fileImport.uploadFailed', rows: [] };
}

/** Whether a failed file import is "the scan has not cleared it yet" — and nothing else. */
export function isScanPending(cause: unknown): boolean {
    return (
        cause instanceof HttpErrorResponse &&
        cause.status === HTTP_CONFLICT &&
        (cause.error as { messageKey?: unknown } | null)?.messageKey === SCAN_PENDING_KEY
    );
}

/** The wait before retry number `attempt` (1-based): 2 s, 4 s, 8 s, then 15 s. */
export function scanPendingRetryDelayMs(attempt: number): number {
    return Math.min(SCAN_RETRY_CAP_MS, SCAN_RETRY_FIRST_MS * 2 ** Math.max(0, attempt - 1));
}
